import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const UserContext = createContext(null);

// Decode JWT payload without verification (client-side only for expiry check)
const getTokenExpiry = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp ? payload.exp * 1000 : null; // convert to ms
  } catch {
    return null;
  }
};

export const UserProvider = ({ children }) => {
  // Restore synchronously so guarded routes (e.g. /admin) don't redirect
  // on the first render after a page refresh
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("skillyer_user"));
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(localStorage.getItem("skillyer_user_token"));

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("skillyer_user_token");
    localStorage.removeItem("skillyer_user");
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem("skillyer_user");
    if (stored && token) {
      try {
        setUser(JSON.parse(stored));
      } catch {
        localStorage.removeItem("skillyer_user");
      }
    }
  }, [token]);

  // Auto-logout when token expires
  useEffect(() => {
    if (!token) return;
    const expiry = getTokenExpiry(token);
    if (!expiry) return;
    const remaining = expiry - Date.now();
    if (remaining <= 0) { logout(); return; }
    const timer = setTimeout(() => logout(), remaining);
    return () => clearTimeout(timer);
  }, [token, logout]);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem("skillyer_user_token", authToken);
    localStorage.setItem("skillyer_user", JSON.stringify(userData));
  };

  const isAdmin = user?.role === "admin";
  const isBlogAdmin = user?.role === "blog-admin";

  return (
    <UserContext.Provider
      value={{ user, token, login, logout, isLoggedIn: !!token && !!user, isAdmin, isBlogAdmin }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
